import { useEffect } from 'react';
import { CATEGORY_LABEL, IMPACT_META, type MarketEvent } from '@/mocks/marketCalendar';
import { minutesToLabel, toUtcMinutes } from '../calculations';
import { CurrencyFlag, ImpactBadge } from './ui';

export default function EventDrawer({
  event,
  toDisplay,
  onClose,
}: {
  event: MarketEvent | null;
  toDisplay: (utc: number) => number;
  onClose: () => void;
}) {
  useEffect(() => {
    if (event) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [event]);

  if (!event) return null;

  const meta = IMPACT_META[event.impact];
  const displayTime = minutesToLabel(toDisplay(toUtcMinutes(event.time)));

  const fields = [
    { label: 'Currency', value: event.currency },
    { label: 'Time', value: `${displayTime} · ${event.time} UTC` },
    { label: 'Impact', value: meta.label },
    { label: 'Previous', value: event.previous },
    { label: 'Forecast', value: event.forecast },
    { label: 'Actual', value: event.actual ?? '—' },
  ];

  return (
    <div className="fixed inset-0 z-[60]">
      {/* backdrop */}
      <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-sm" onClick={onClose} />

      {/* panel */}
      <div className="absolute bottom-0 right-0 top-0 flex w-full max-w-md flex-col bg-white shadow-2xl md:bottom-0">
        <div className="flex items-start gap-3 border-b border-slate-200/80 p-5">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-50 text-2xl">
            <CurrencyFlag code={event.currency} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-slate-500">{event.currency}</span>
              <ImpactBadge impact={event.impact} />
            </div>
            <h3 className="mt-1 font-display text-lg font-bold leading-tight text-slate-900">{event.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* key fields */}
          <div className="grid grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.label} className="rounded-xl border border-slate-200/80 bg-slate-50/50 p-3">
                <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{f.label}</p>
                <p className={`mt-1 font-mono text-sm font-bold tabular-nums ${f.label === 'Actual' && event.actual ? 'text-emerald-600' : 'text-slate-800'}`}>
                  {f.value}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-400">
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">{CATEGORY_LABEL[event.category]}</span>
            <span>{event.country}</span>
          </div>

          {/* about */}
          <div className="mt-6">
            <h4 className="text-sm font-bold text-slate-800">About this event</h4>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{event.description}</p>
          </div>

          {/* previous releases */}
          {event.history.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-bold text-slate-800">Previous Releases</h4>
              <div className="mt-3 overflow-hidden rounded-xl border border-slate-200/80">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-400">
                      <th className="px-3 py-2 font-medium">Kỳ</th>
                      <th className="px-3 py-2 font-medium">Actual</th>
                      <th className="px-3 py-2 font-medium">Forecast</th>
                      <th className="px-3 py-2 font-medium">Previous</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.history.map((h) => (
                      <tr key={h.date} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-500">{h.date}</td>
                        <td className="px-3 py-2 font-mono font-bold text-emerald-600">{h.actual}</td>
                        <td className="px-3 py-2 font-mono text-slate-500">{h.forecast}</td>
                        <td className="px-3 py-2 font-mono text-slate-500">{h.previous}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <p className="mt-6 text-[11px] leading-relaxed text-slate-400">
            Dữ liệu chỉ nhằm mục đích thông tin. Thời gian và giá trị có thể thay đổi theo nguồn dữ liệu.
          </p>
        </div>
      </div>
    </div>
  );
}